import { useRef, useState } from "react";
import { ExternalLink, FileUp, Loader2, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";

export const RUGBY_STATUS = {
  found: ["Found", "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"],
  not_found: ["Not found", "text-red-400 border-red-500/30 bg-red-500/10"],
  needs_review: ["Needs review", "text-amber-400 border-amber-500/30 bg-amber-500/10"],
  pending: ["Pending", "text-slate-400 border-slate-600 bg-slate-500/10"],
};

const ago = (iso) => (iso ? `${fmtDate(iso)} ${new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "—");

export const RugbyRow = ({ t, running, onScrape, onChanged }) => {
  const [url, setUrl] = useState(t.roster_url_override || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [label, cls] = RUGBY_STATUS[t.status] || RUGBY_STATUS.pending;
  const source = t.roster_source === "csv" ? null : t.roster_url || t.source_url;

  const saveAndScrape = async () => {
    try {
      await api.put(`/admin/rugby/teams/${t.slug}`, { roster_url: url.trim() });
      await onScrape([t.slug], t.school);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/admin/rugby/teams/${t.slug}/csv`, fd);
      toast.success(`${t.school}: ${r.data.players} players imported`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
    fileRef.current.value = "";
  };

  return (
    <tr data-testid={`rugby-team-row-${t.slug}`} className="align-top">
      <td className="px-4 py-3">
        <div className="font-semibold text-white">{t.school}</div>
        <div className="text-xs text-slate-500">{t.location || "—"}</div>
        <div className="mt-1 flex flex-wrap gap-1">
          {(t.organizations || []).map((o) => <span key={o} className="rounded border border-slate-700 px-1.5 font-data text-[10px] text-slate-300">{o}</span>)}
          {(t.formats || []).map((f) => <span key={f} className="rounded bg-slate-800 px-1.5 font-data text-[10px] text-slate-400">{f}</span>)}
        </div>
      </td>
      <td className="px-4 py-3 text-xs">
        <div className="font-data text-slate-200">{t.division || "—"}</div>
        <div className="mt-0.5 max-w-[200px] text-slate-500">{t.conference}</div>
      </td>
      <td className="px-4 py-3">
        <span data-testid={`rugby-team-status-${t.slug}`} className={`whitespace-nowrap rounded-full border px-2.5 py-0.5 font-data text-[11px] uppercase ${cls}`}>{label}</span>
        {t.error && <div className="mt-1 max-w-[200px] text-[11px] text-slate-500">{t.error}</div>}
      </td>
      <td data-testid={`rugby-team-players-${t.slug}`} className="px-4 py-3 font-data">{t.players || 0}</td>
      <td className="px-4 py-3 text-xs">
        {t.roster_source === "csv" ? <span className="text-slate-300">CSV upload{t.csv_filename ? ` · ${t.csv_filename}` : ""}</span>
          : source ? <a data-testid={`rugby-team-source-${t.slug}`} href={source} target="_blank" rel="noopener noreferrer" className="inline-flex max-w-[220px] items-center gap-1 truncate text-[#FF2D78] hover:underline"><ExternalLink className="h-3 w-3 shrink-0" /><span className="truncate">{source.replace(/^https?:\/\//, "")}</span></a>
            : <span className="text-slate-600">—</span>}
        <div className="mt-1 font-data text-[10px] text-slate-500">{ago(t.last_scraped_at)}</div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <input data-testid={`rugby-team-url-${t.slug}`} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste roster link"
            className="h-8 w-48 rounded-md border border-slate-800 bg-[#0B0F19] px-2.5 text-xs outline-none focus:border-[#FF2D78]" />
          <button data-testid={`rugby-team-save-scrape-${t.slug}`} title="Save link and scrape" disabled={running || !/^https?:\/\//.test(url.trim())} onClick={saveAndScrape}
            className="rounded-md p-1.5 text-slate-300 hover:bg-slate-800 disabled:opacity-30"><Send className="h-4 w-4" /></button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid={`rugby-team-csv-input-${t.slug}`} onChange={(e) => upload(e.target.files?.[0])} />
          <button data-testid={`rugby-team-csv-${t.slug}`} title="Upload CSV roster" disabled={busy} onClick={() => fileRef.current.click()}
            className="rounded-md p-1.5 text-slate-300 hover:bg-slate-800 disabled:opacity-30">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}</button>
          <button data-testid={`rugby-team-scrape-${t.slug}`} title="Re-scrape this team" disabled={running} onClick={() => onScrape([t.slug], t.school)}
            className="rounded-md p-1.5 text-slate-300 hover:bg-slate-800 disabled:opacity-30"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </td>
    </tr>
  );
};
